import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/CustomAuthContext';
import { useNavigate } from 'react-router-dom';
import { itemsAPI, pokemonAPI, abilitiesAPI, ItemMaster, PokemonMaster } from '../../services/api';
import SearchableDropdown from '../Helper/SearchableDropdown';
// import ImageRecognition from '../Damage-Calc/ImageRecognition'; // Disabled while training AI
import { calculateHiddenPower, formatHiddenPower } from '../../utils/hiddenPower';
import './SubmitData.css';

// Pokemon data constants
const POKEMON_TYPES = [
  'None', 'Normal', 'Fire', 'Water', 'Electric', 'Grass', 'Ice',
  'Fighting', 'Poison', 'Ground', 'Flying', 'Psychic', 'Bug',
  'Rock', 'Ghost', 'Dragon', 'Dark', 'Steel', 'Fairy'
];

const POKEMON_NATURES = [
  'Hardy', 'Lonely', 'Brave', 'Adamant', 'Naughty', 'Bold', 'Docile', 'Relaxed', 'Impish', 'Lax',
  'Timid', 'Hasty', 'Serious', 'Jolly', 'Naive', 'Modest', 'Mild', 'Quiet', 'Bashful', 'Rash',
  'Calm', 'Gentle', 'Sassy', 'Careful', 'Quirky'
];

// Neutral natures that don't affect stats
const NEUTRAL_NATURES = ['Hardy', 'Docile', 'Serious', 'Bashful', 'Quirky'];

// Helper function to display nature names
const formatNatureName = (nature: string): string => {
  return NEUTRAL_NATURES.includes(nature) ? 'Neutral' : nature;
};

interface SubmissionData {
  type: 'Pokemon' | 'Item';
  name: string;
  price: string;
  dateSold: string;
  proofUrl: string;
  // Pokemon specific fields
  pokemonStats?: {
    hp: string;
    attack: string;
    defense: string;
    spAttack: string;
    spDefense: string;
    speed: string;
    nature: string;
    ability: string;
    form: string;
  };
}

const SubmitData: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Utility function to format price with thousands separators
  const formatPrice = (value: string): string => {
    // Remove all non-digit characters except decimal point
    const cleanValue = value.replace(/[^\d.]/g, '');

    // Split by decimal point
    const parts = cleanValue.split('.');

    // Add thousands separators to the integer part
    if (parts[0]) {
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    // Return formatted value (only integer part for our use case)
    return parts[0] || '';
  };

  // Utility function to validate and cap IV values
  const validateIV = (value: string): string => {
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 0) return '';
    if (numValue > 31) return '31';
    return numValue.toString();
  };

  // Search functions for dropdowns
  const searchPokemon = async (query: string): Promise<{ id: number; name: string }[]> => {
    try {
      console.log('Searching Pokemon with query:', query);
      const results = await pokemonAPI.search(query, 50);
      console.log('Pokemon search results:', results);
      return results.map((pokemon: PokemonMaster) => ({
        id: pokemon.pokemon_id,
        name: pokemon.pokemon_name
      }));
    } catch (error) {
      console.error('Error searching Pokemon:', error);
      return [];
    }
  };

  const searchItems = async (query: string): Promise<{ id: number; name: string }[]> => {
    try {
      const results = await itemsAPI.search(query, 50);
      return results.map((item: ItemMaster) => ({
        id: item.item_id,
        name: item.item_name
      }));
    } catch (error) {
      console.error('Error searching Items:', error);
      return [];
    }
  };

  const searchNature = async (query: string): Promise<{ id: number; name: string }[]> => {
    const queryLower = query.toLowerCase();

    // Create unique nature options (combine neutral natures into one "Neutral" option)
    const uniqueNatures: { nature: string; index: number; displayName: string }[] = [];
    const seenDisplayNames = new Set<string>();

    POKEMON_NATURES.forEach((nature, index) => {
      const displayName = formatNatureName(nature);
      if (!seenDisplayNames.has(displayName)) {
        seenDisplayNames.add(displayName);
        uniqueNatures.push({ nature, index, displayName });
      }
    });

    return uniqueNatures
      .filter(({ nature, displayName }) => {
        const natureLower = nature.toLowerCase();
        const displayLower = displayName.toLowerCase();

        // Check if query matches the nature name or display name
        const directMatch = natureLower.includes(queryLower) || displayLower.includes(queryLower);

        // Check if query matches a neutral nature name (like "bashful") and this is the Neutral entry
        const neutralMatch = displayName === 'Neutral' &&
          NEUTRAL_NATURES.some(neutralNature => neutralNature.toLowerCase().includes(queryLower));

        return directMatch || neutralMatch;
      })
      .map(({ index, displayName }) => ({
        id: index + 1,
        name: displayName
      }));
  };

  const searchAbility = async (query: string): Promise<{ id: number; name: string }[]> => {
    const queryLower = query.toLowerCase();

    return abilities
      .filter(ability =>
        ability.ability_name.toLowerCase().includes(queryLower)
      )
      .map(ability => ({
        id: ability.ability_id,
        name: ability.ability_name
      }));
  };

  const [submissionData, setSubmissionData] = useState<SubmissionData>({
    type: 'Item',
    name: '',
    price: '',
    dateSold: '',
    proofUrl: '',
    pokemonStats: {
      hp: '',
      attack: '',
      defense: '',
      spAttack: '',
      spDefense: '',
      speed: '',
      nature: '',
      ability: '',
      form: ''
    }
  });

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [calculatedHiddenPower, setCalculatedHiddenPower] = useState<string | null>(null);
  const [abilities, setAbilities] = useState<{ ability_id: number; ability_name: string }[]>([]);

  // Calculate Hidden Power whenever IVs change
  useEffect(() => {
    if (submissionData.type === 'Pokemon' && submissionData.pokemonStats) {
      const ivs = {
        hp: submissionData.pokemonStats.hp ? parseInt(submissionData.pokemonStats.hp) : undefined,
        attack: submissionData.pokemonStats.attack ? parseInt(submissionData.pokemonStats.attack) : undefined,
        defense: submissionData.pokemonStats.defense ? parseInt(submissionData.pokemonStats.defense) : undefined,
        spAttack: submissionData.pokemonStats.spAttack ? parseInt(submissionData.pokemonStats.spAttack) : undefined,
        spDefense: submissionData.pokemonStats.spDefense ? parseInt(submissionData.pokemonStats.spDefense) : undefined,
        speed: submissionData.pokemonStats.speed ? parseInt(submissionData.pokemonStats.speed) : undefined,
      };

      const hiddenPowerResult = calculateHiddenPower(ivs);
      const formattedHP = formatHiddenPower(hiddenPowerResult);
      setCalculatedHiddenPower(formattedHP);
    }
  }, [
    submissionData.type,
    submissionData.pokemonStats?.hp,
    submissionData.pokemonStats?.attack,
    submissionData.pokemonStats?.defense,
    submissionData.pokemonStats?.spAttack,
    submissionData.pokemonStats?.spDefense,
    submissionData.pokemonStats?.speed
  ]);

  // Load abilities on component mount
  useEffect(() => {
    const loadAbilities = async () => {
      try {
        const abilitiesData = await abilitiesAPI.getAll();
        setAbilities(abilitiesData);
      } catch (error) {
        console.error('Failed to load abilities:', error);
      }
    };

    loadAbilities();
  }, []);

  const handleInputChange = (field: string, value: string) => {
    setSubmissionData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePokemonStatChange = (stat: string, value: string) => {
    setSubmissionData(prev => ({
      ...prev,
      pokemonStats: {
        ...prev.pokemonStats!,
        [stat]: value
      }
    }));
  };

  // Handle stats recognized from image - Disabled while training AI
  /*
  const handleStatsRecognized = (recognizedStats: any) => {
    setSubmissionData(prev => ({
      ...prev,
      // If Pokemon name is recognized and we don't have one selected, set it
      name: recognizedStats.name && !prev.name ? recognizedStats.name : prev.name,
      pokemonStats: {
        ...prev.pokemonStats!,
        // Update stats that were successfully recognized
        hp: recognizedStats.hp ? recognizedStats.hp.toString() : prev.pokemonStats?.hp || '',
        attack: recognizedStats.attack ? recognizedStats.attack.toString() : prev.pokemonStats?.attack || '',
        defense: recognizedStats.defense ? recognizedStats.defense.toString() : prev.pokemonStats?.defense || '',
        spAttack: recognizedStats.spAttack ? recognizedStats.spAttack.toString() : prev.pokemonStats?.spAttack || '',
        spDefense: recognizedStats.spDefense ? recognizedStats.spDefense.toString() : prev.pokemonStats?.spDefense || '',
        speed: recognizedStats.speed ? recognizedStats.speed.toString() : prev.pokemonStats?.speed || '',
        nature: recognizedStats.nature || prev.pokemonStats?.nature || '',
        ability: recognizedStats.ability || prev.pokemonStats?.ability || '',
      }
    }));

    // Show success message
    setSuccessMessage('Pokemon stats recognized and filled automatically! Please review and adjust if needed.');
    setTimeout(() => setSuccessMessage(null), 5000);
  };
  */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    setLoading(true);

    // Basic validation
    if (!submissionData.name || !submissionData.price || !submissionData.dateSold) {
      setErrorMessage('Please fill in all required fields');
      setLoading(false);
      return;
    }

    // Pokemon-specific validation
    if (submissionData.type === 'Pokemon' && !submissionData.pokemonStats?.nature) {
      setErrorMessage('Nature is required for Pokemon submissions');
      setLoading(false);
      return;
    }

    if (submissionData.type === 'Pokemon' && !submissionData.pokemonStats?.ability) {
      setErrorMessage('Ability is required for Pokemon submissions');
      setLoading(false);
      return;
    }

    if (parseFloat(submissionData.price) <= 0) {
      setErrorMessage('Price must be a positive number');
      setLoading(false);
      return;
    }

    if (!user) {
      setErrorMessage('You must be logged in to submit data');
      setLoading(false);
      return;
    }

    try {
      if (submissionData.type === 'Pokemon') {
        // Debug: Log the pokemonStats object
        console.log('submissionData.pokemonStats:', submissionData.pokemonStats);

        const pokemonData = {
          pokemon_name: submissionData.name,
          price: parseFloat(submissionData.price),
          date_sold: submissionData.dateSold,
          proof_url: submissionData.proofUrl || undefined,
          nature: submissionData.pokemonStats?.nature || undefined,
          ability: submissionData.pokemonStats?.ability || undefined,
          form: submissionData.pokemonStats?.form || undefined,
          iv_hp: submissionData.pokemonStats?.hp !== undefined && submissionData.pokemonStats?.hp !== '' ? parseInt(submissionData.pokemonStats.hp) : undefined,
          iv_attack: submissionData.pokemonStats?.attack !== undefined && submissionData.pokemonStats?.attack !== '' ? parseInt(submissionData.pokemonStats.attack) : undefined,
          iv_defense: submissionData.pokemonStats?.defense !== undefined && submissionData.pokemonStats?.defense !== '' ? parseInt(submissionData.pokemonStats.defense) : undefined,
          iv_sp_attack: submissionData.pokemonStats?.spAttack !== undefined && submissionData.pokemonStats?.spAttack !== '' ? parseInt(submissionData.pokemonStats.spAttack) : undefined,
          iv_sp_defense: submissionData.pokemonStats?.spDefense !== undefined && submissionData.pokemonStats?.spDefense !== '' ? parseInt(submissionData.pokemonStats.spDefense) : undefined,
          iv_speed: submissionData.pokemonStats?.speed !== undefined && submissionData.pokemonStats?.speed !== '' ? parseInt(submissionData.pokemonStats.speed) : undefined,
        };

        // Debug: Log the final pokemonData object
        console.log('Final pokemonData being sent:', pokemonData);

        const response = await pokemonAPI.submit(pokemonData);
        setSuccessMessage(`Pokemon sale submitted successfully! (ID: ${response.sale_id})`);
      } else {
        const itemData = {
          item_name: submissionData.name,
          price: parseFloat(submissionData.price),
          date_sold: submissionData.dateSold,
          proof_url: submissionData.proofUrl || undefined,
        };

        const response = await itemsAPI.submit(itemData);
        setSuccessMessage(`Item sale submitted successfully! (ID: ${response.sale_id})`);
      }

      // Reset form after success, preserving the current submission type
      const currentType = submissionData.type;
      setTimeout(() => {
        setSuccessMessage(null);
        setSubmissionData({
          type: currentType,
          name: '',
          price: '',
          dateSold: '',
          proofUrl: '',
          pokemonStats: {
            hp: '', attack: '', defense: '', spAttack: '', spDefense: '', speed: '',
            nature: '', ability: '', form: ''
          }
        });
      }, 3000);

    } catch (err: any) {
      setErrorMessage(err.response?.data?.error || 'Failed to submit data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (successMessage) {
    return (
      <div className="submit-data-container">
        <div className="success-message">
          <div className="success-icon">✅</div>
          <h2>Submission Successful!</h2>
          <p>{successMessage}</p>
          <p>Your data has been submitted for review. A checker will verify the information and approve it if everything looks good.</p>
          <div className="success-actions">
            <button onClick={() => setSuccessMessage(null)} className="submit-another-button">
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="submit-data-container">
      <header className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1>📝 Submit Data</h1>
            <p>Upload auction data for price tracking</p>
          </div>
        </div>
      </header>

      <main className="submit-data-main">
        {/* Navigation Buttons */}
        <div className="filter-buttons">
          <button
            type="button"
            onClick={() => handleInputChange('type', 'Item')}
            className={`submit-data-filter-button ${submissionData.type === 'Item' ? 'active' : ''}`}
          >
            📦 Items
          </button>
          <button
            type="button"
            onClick={() => handleInputChange('type', 'Pokemon')}
            className={`submit-data-filter-button ${submissionData.type === 'Pokemon' ? 'active' : ''}`}
          >
            🐾 Pokemon
          </button>
        </div>

        <form onSubmit={handleSubmit} className="submission-form">
          {errorMessage && <div className="error-message">{errorMessage}</div>}

          <div className="form-section">
            <h3>Basic Information</h3>

            <SearchableDropdown
              label={submissionData.type === 'Pokemon' ? 'Pokemon Name' : 'Item Name'}
              value={submissionData.name}
              onChange={(value) => handleInputChange('name', value)}
              placeholder={`Search and select ${submissionData.type}...`}
              searchFunction={submissionData.type === 'Pokemon' ? searchPokemon : searchItems}
              required={true}
            />

            <div className="form-group">
              <label>Price (Pokedollars) *</label>
              <input
                type="text"
                value={submissionData.price ? formatPrice(submissionData.price.toString()) : ''}
                onChange={(e) => {
                  const cleanValue = e.target.value.replace(/[^\d]/g, '');
                  handleInputChange('price', cleanValue);
                }}
                placeholder="Enter sale price..."
                required
              />
              <small>Price will be automatically formatted (e.g., 1,000,000)</small>
            </div>

            <div className="form-group">
              <label>Date Sold *</label>
              <input
                type="date"
                value={submissionData.dateSold}
                onChange={(e) => handleInputChange('dateSold', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Proof URL</label>
              <input
                type="url"
                value={submissionData.proofUrl}
                onChange={(e) => handleInputChange('proofUrl', e.target.value)}
                placeholder="Screenshot or auction link..."
              />
              <small>Provide a screenshot or link to the auction as proof</small>
            </div>
          </div>

          {submissionData.type === 'Pokemon' && (
            <div className="form-section">
              <h3>Pokemon Stats</h3>

              {/* Image Recognition Component - Disabled while training AI */}
              {/* <ImageRecognition onStatsRecognized={handleStatsRecognized} /> */}

              <div className="stats-grid">
                {/* Mandatory IV Fields in Order */}
                <div className="form-group">
                  <label>Attack IV *</label>
                  <input
                    type="number"
                    value={submissionData.pokemonStats?.attack || ''}
                    onChange={(e) => handlePokemonStatChange('attack', validateIV(e.target.value))}
                    placeholder="0-31"
                    min="0"
                    max="31"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Defense IV *</label>
                  <input
                    type="number"
                    value={submissionData.pokemonStats?.defense || ''}
                    onChange={(e) => handlePokemonStatChange('defense', validateIV(e.target.value))}
                    placeholder="0-31"
                    min="0"
                    max="31"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Speed IV *</label>
                  <input
                    type="number"
                    value={submissionData.pokemonStats?.speed || ''}
                    onChange={(e) => handlePokemonStatChange('speed', validateIV(e.target.value))}
                    placeholder="0-31"
                    min="0"
                    max="31"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Sp. Attack IV *</label>
                  <input
                    type="number"
                    value={submissionData.pokemonStats?.spAttack || ''}
                    onChange={(e) => handlePokemonStatChange('spAttack', validateIV(e.target.value))}
                    placeholder="0-31"
                    min="0"
                    max="31"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Sp. Defense IV *</label>
                  <input
                    type="number"
                    value={submissionData.pokemonStats?.spDefense || ''}
                    onChange={(e) => handlePokemonStatChange('spDefense', validateIV(e.target.value))}
                    placeholder="0-31"
                    min="0"
                    max="31"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>HP IV *</label>
                  <input
                    type="number"
                    value={submissionData.pokemonStats?.hp || ''}
                    onChange={(e) => handlePokemonStatChange('hp', validateIV(e.target.value))}
                    placeholder="0-31"
                    min="0"
                    max="31"
                    required
                  />
                </div>

                {/* Optional Fields */}
                <SearchableDropdown
                  label="Nature"
                  value={submissionData.pokemonStats?.nature || ''}
                  onChange={(value) => handlePokemonStatChange('nature', value)}
                  placeholder="Select Nature..."
                  searchFunction={searchNature}
                  required={true}
                />

                <div className="form-group">
                  <label>Hidden Power (Calculated)</label>
                  <div className="calculated-hidden-power">
                    {calculatedHiddenPower ? (
                      <span className="hidden-power-result">
                        🎯 {calculatedHiddenPower}
                      </span>
                    ) : (
                      <span className="hidden-power-placeholder"/* TODO: Make Text Shorter*/>
                        Enter all IVs to calculate Hidden Power
                      </span>
                    )}
                  </div>
                </div>

                <SearchableDropdown
                  label="Ability"
                  value={submissionData.pokemonStats?.ability || ''}
                  onChange={(value) => handlePokemonStatChange('ability', value)}
                  placeholder="Select Ability..."
                  searchFunction={searchAbility}
                  required={true}
                />

                <div className="form-group">
                  <label>Form *</label>
                  <input
                    type="text"
                    value={submissionData.pokemonStats?.form || ''}
                    onChange={(e) => handlePokemonStatChange('form', e.target.value)}
                    placeholder="e.g., Shiny, Normal..."
                    required
                  />
                </div>
              </div>
              <small>* Required fields for Pokemon submissions</small>
            </div>
          )}

          <div className="form-actions">
            <button type="submit" disabled={loading} className="submit-button">
              {loading ? 'Submitting...' : 'Submit for Review'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default SubmitData;
